import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Lock, LockOpen, FileText, Bank, CheckCircle, WarningCircle, Upload, ShieldCheck, ArrowRight } from "@phosphor-icons/react";

const fmt = (n) => new Intl.NumberFormat("es-MX").format(Math.round(n || 0));

const FALLBACK_TERMS = `Cargando términos y condiciones...`;
const FALLBACK_PRIVACY = `Cargando aviso de privacidad...`;

export default function UnlockWallet({ open, onClose, balance, onSuccess }) {
  const [step, setStep] = useState(1);
  const [status, setStatus] = useState(null);
  const [termsScrolled, setTermsScrolled] = useState(false);
  const [privacyScrolled, setPrivacyScrolled] = useState(false);
  const [termsCheck, setTermsCheck] = useState(false);
  const [privacyCheck, setPrivacyCheck] = useState(false);
  const [proofFile, setProofFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [termsText, setTermsText] = useState(FALLBACK_TERMS);
  const [privacyText, setPrivacyText] = useState(FALLBACK_PRIVACY);
  const [termsTitle, setTermsTitle] = useState("Términos y Condiciones");
  const [privacyTitle, setPrivacyTitle] = useState("Aviso de Privacidad");

  // Carga textos legales desde el backend (editables por admin)
  useEffect(() => {
    if (!open) return;
    api.get("/legal/terms_dyd")
      .then((r) => {
        setTermsText(r.data?.content || FALLBACK_TERMS);
        setTermsTitle(r.data?.title || "Términos y Condiciones");
      })
      .catch(() => setTermsText(FALLBACK_TERMS));
    api.get("/legal/privacy_dyd")
      .then((r) => {
        setPrivacyText(r.data?.content || FALLBACK_PRIVACY);
        setPrivacyTitle(r.data?.title || "Aviso de Privacidad");
      })
      .catch(() => setPrivacyText(FALLBACK_PRIVACY));
  }, [open]);

  const loadStatus = async () => {
    try {
      const r = await api.get("/dyd-coin/unlock/status");
      setStatus(r.data);
      // Pre-seleccionar paso según estado actual
      if (r.data.kyc_status === "approved") {
        if (onSuccess) onSuccess();
        onClose();
        return;
      }
      if (r.data.kyc_status === "proof_uploaded") setStep(4);
      else if (r.data.kyc_status === "terms_accepted" || r.data.terms_accepted_at) setStep(3);
      else setStep(1);
    } catch (e) { /* ignore */ }
  };
  useEffect(() => { if (open) loadStatus(); }, [open]);

  const acceptTerms = async () => {
    if (!termsCheck || !privacyCheck) { toast.error("Debes aceptar ambos documentos"); return; }
    try {
      await api.post("/dyd-coin/unlock/accept-terms", { accept_terms: true, accept_privacy: true });
      toast.success("Términos aceptados");
      setStep(3);
      loadStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    }
  };

  const uploadProof = async () => {
    if (!proofFile) { toast.error("Selecciona un comprobante"); return; }
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", proofFile);
      await api.post("/dyd-coin/unlock/upload-proof", form, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Comprobante enviado. En revisión.");
      setStep(4);
      loadStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al subir");
    } finally { setUploading(false); }
  };

  const requiredMxn = status?.required_mxn ?? Math.round((balance || 0) * 0.85 * 100) / 100;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl bg-[#0A0A0A] border-[#CCFF00]/30 max-h-[90vh] overflow-y-auto" data-testid="unlock-wallet-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-['Barlow_Condensed'] text-3xl font-black text-white">
            <Lock size={24} weight="fill" className="text-[#CCFF00]" /> Activar DYD Moneda
          </DialogTitle>
        </DialogHeader>

        {/* Stepper */}
        <div className="flex items-center justify-between text-[10px] uppercase tracking-widest font-bold text-gray-500 mb-2">
          {["Términos", "Privacidad", "Recarga", "En revisión"].map((label, i) => {
            const idx = i + 1;
            const active = step === idx;
            const done = step > idx;
            return (
              <div key={label} className="flex-1 flex items-center">
                <div className={`flex items-center gap-1 ${active ? "text-[#CCFF00]" : done ? "text-green-400" : ""}`}>
                  <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-black ${active ? "bg-[#CCFF00] text-black" : done ? "bg-green-500/20 text-green-400" : "bg-white/10"}`}>{done ? <CheckCircle size={12} weight="fill" /> : idx}</span>
                  <span className="hidden sm:inline">{label}</span>
                </div>
                {idx < 4 && <div className={`flex-1 h-px mx-2 ${done ? "bg-green-400/40" : "bg-white/10"}`} />}
              </div>
            );
          })}
        </div>

        {/* STEP 1: TERMS */}
        {step === 1 && (
          <div className="space-y-3">
            <div className="flex items-center gap-2"><FileText size={18} className="text-[#CCFF00]" /><h3 className="text-sm font-bold text-white">{termsTitle}</h3></div>
            <div
              className="bg-black/50 border border-white/10 p-3 rounded text-[11px] text-gray-300 leading-relaxed whitespace-pre-line max-h-[280px] overflow-y-auto"
              onScroll={(e) => { const el = e.target; if (el.scrollTop + el.clientHeight >= el.scrollHeight - 20) setTermsScrolled(true); }}
              data-testid="terms-scroll-box"
            >
              {termsText}
            </div>
            {!termsScrolled && <p className="text-[10px] text-yellow-400 flex items-center gap-1"><WarningCircle size={12} /> Desliza hasta el final para continuar</p>}
            <label className={`flex items-center gap-2 p-2 rounded border cursor-pointer ${termsScrolled ? "border-[#CCFF00]/40 hover:bg-[#CCFF00]/5" : "border-white/10 opacity-50 cursor-not-allowed"}`}>
              <Checkbox checked={termsCheck} onCheckedChange={(c) => termsScrolled && setTermsCheck(!!c)} disabled={!termsScrolled} data-testid="terms-check" />
              <span className="text-xs text-white font-semibold">He leído y acepto los Términos y Condiciones</span>
            </label>
            <Button disabled={!termsCheck} onClick={() => setStep(2)} className="w-full bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase" data-testid="terms-continue">
              Continuar <ArrowRight size={14} className="ml-1" />
            </Button>
          </div>
        )}

        {/* STEP 2: PRIVACY */}
        {step === 2 && (
          <div className="space-y-3">
            <div className="flex items-center gap-2"><ShieldCheck size={18} className="text-[#CCFF00]" /><h3 className="text-sm font-bold text-white">{privacyTitle}</h3></div>
            <div
              className="bg-black/50 border border-white/10 p-3 rounded text-[11px] text-gray-300 leading-relaxed whitespace-pre-line max-h-[280px] overflow-y-auto"
              onScroll={(e) => { const el = e.target; if (el.scrollTop + el.clientHeight >= el.scrollHeight - 20) setPrivacyScrolled(true); }}
              data-testid="privacy-scroll-box"
            >
              {privacyText}
            </div>
            {!privacyScrolled && <p className="text-[10px] text-yellow-400 flex items-center gap-1"><WarningCircle size={12} /> Desliza hasta el final para continuar</p>}
            <label className={`flex items-center gap-2 p-2 rounded border cursor-pointer ${privacyScrolled ? "border-[#CCFF00]/40 hover:bg-[#CCFF00]/5" : "border-white/10 opacity-50 cursor-not-allowed"}`}>
              <Checkbox checked={privacyCheck} onCheckedChange={(c) => privacyScrolled && setPrivacyCheck(!!c)} disabled={!privacyScrolled} data-testid="privacy-check" />
              <span className="text-xs text-white font-semibold">He leído y acepto el Aviso de Privacidad</span>
            </label>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setStep(1)} className="border-white/15">Atrás</Button>
              <Button disabled={!privacyCheck} onClick={acceptTerms} className="flex-1 bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase" data-testid="privacy-continue">
                Aceptar y continuar <ArrowRight size={14} className="ml-1" />
              </Button>
            </div>
          </div>
        )}

        {/* STEP 3: TOP-UP */}
        {step === 3 && (
          <div className="space-y-3">
            <div className="flex items-center gap-2"><Bank size={18} className="text-[#CCFF00]" /><h3 className="text-sm font-bold text-white">Recarga bancaria — Respaldar tu wallet</h3></div>
            <div className="bg-gradient-to-br from-[#CCFF00]/10 to-amber-500/5 border border-[#CCFF00]/30 p-4 rounded">
              <p className="text-[10px] uppercase tracking-widest font-bold text-gray-400">Saldo DYD actual</p>
              <p className="font-['Barlow_Condensed'] text-5xl font-black text-[#CCFF00] leading-none">{fmt(balance || status?.balance || 0)} DYD</p>
              <div className="mt-3 border-t border-white/10 pt-3">
                <p className="text-[10px] uppercase tracking-widest font-bold text-gray-400">Recarga requerida</p>
                <p className="font-['Barlow_Condensed'] text-4xl font-black text-white leading-none">${fmt(requiredMxn)} <span className="text-base">MXN</span></p>
              </div>
            </div>
            <div className="bg-[#0A0A0A] border border-white/10 p-3 rounded text-xs space-y-1">
              <p className="text-[10px] uppercase tracking-widest font-bold text-[#CCFF00]">Datos para transferencia bancaria</p>
              <p className="text-gray-300">Beneficiario: <span className="text-white font-bold">Disfrutaydiviertete.com</span></p>
              <p className="text-gray-300">Banco: <span className="text-white font-bold">BBVA</span></p>
              <p className="text-gray-300">CLABE: <span className="text-white font-bold font-mono">012180001234567890</span></p>
              <p className="text-gray-300">Cuenta: <span className="text-white font-bold font-mono">0123456789</span></p>
              <p className="text-gray-300">Referencia: <span className="text-[#CCFF00] font-bold font-mono">DYD-{(status?.balance || balance || 0)}-{Date.now().toString().slice(-6)}</span></p>
              <p className="text-[10px] text-gray-500 mt-2">⚠ Después de transferir, sube tu comprobante (PDF/JPG/PNG, máx 10MB).</p>
            </div>
            <div>
              <label className="block">
                <input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp,.heic" onChange={(e) => setProofFile(e.target.files?.[0] || null)} className="hidden" data-testid="proof-file-input" id="proof-input" />
                <div className={`flex items-center justify-center gap-2 border-2 border-dashed py-6 rounded cursor-pointer transition-colors ${proofFile ? "border-green-500/50 bg-green-500/5" : "border-white/15 hover:border-[#CCFF00]/40"}`} onClick={() => document.getElementById("proof-input").click()}>
                  <Upload size={18} className={proofFile ? "text-green-400" : "text-[#CCFF00]"} />
                  <span className="text-xs text-white font-semibold">{proofFile ? proofFile.name : "Click para subir comprobante"}</span>
                </div>
              </label>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setStep(2)} className="border-white/15">Atrás</Button>
              <Button disabled={!proofFile || uploading} onClick={uploadProof} className="flex-1 bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase" data-testid="upload-proof-btn">
                {uploading ? "Subiendo..." : "Enviar comprobante"}
              </Button>
            </div>
          </div>
        )}

        {/* STEP 4: AWAIT REVIEW or REJECTED */}
        {step === 4 && (
          <div className="space-y-3" data-testid="step-review">
            {status?.kyc_status === "rejected" ? (
              <>
                <div className="bg-red-500/10 border border-red-500/30 p-4 rounded">
                  <div className="flex items-center gap-2"><WarningCircle size={18} weight="fill" className="text-red-400" /><h3 className="font-bold text-red-300">Comprobante rechazado</h3></div>
                  <p className="text-xs text-red-200/80 mt-1">Motivo: {status.rejection_reason || "—"}</p>
                </div>
                <Button onClick={() => setStep(3)} className="w-full bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase">Reintentar</Button>
              </>
            ) : (
              <>
                <div className="bg-blue-500/10 border border-blue-500/30 p-4 rounded text-center">
                  <CheckCircle size={32} weight="fill" className="text-blue-400 mx-auto" />
                  <p className="font-bold text-blue-300 mt-2">Comprobante enviado</p>
                  <p className="text-xs text-blue-200/80 mt-1">El admin revisará tu pago. Te notificaremos al aprobar.</p>
                  <p className="text-[10px] text-gray-500 mt-2">Enviado: {status?.submitted_at && new Date(status.submitted_at).toLocaleString("es-MX")}</p>
                </div>
                <Button onClick={onClose} variant="outline" className="w-full border-white/15">Cerrar</Button>
              </>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
